<script setup lang="ts">
import { Download, X } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, defineAsyncComponent, onMounted, ref, shallowRef } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import { useMcpApp } from "@/api/apps";
import { isProblem } from "@/api/client";
import { closeRemoteSession, isOpen, recordingUrl, remoteApp, remoteTicket, useRemoteSession, useRemoteTranscript, type RemoteConnectResult } from "@/api/remote";
import CodeBlock from "@/components/common/CodeBlock.vue";
import DescriptionList, { type DescriptionItem } from "@/components/common/DescriptionList.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { useSession } from "@/composables/useSession";
import { remote as t } from "@/copy/remote";
import { errorMessage } from "@/lib/errors";
import { formatDateTime, formatDuration } from "@/lib/format";
import { formatBytes, statusTone } from "@/lib/remote";

definePage({ meta: { title: "Remote session", requires: { anyCapability: ["remote.exec", "remote.read"] } } });

// The ext-apps host is only loaded here (the SDK is ~60 KB gzipped).
const McpAppFrame = defineAsyncComponent(() => import("@/components/apps/McpAppFrame.vue"));

const route = useRoute("/remote/[id]");
const id = computed(() => route.params.id);
const { me } = useSession();
const queryClient = useQueryClient();
const session = useRemoteSession(id);
const data = computed(() => session.data.value);
const mine = computed(() => Boolean(data.value) && (!data.value!.owner || data.value!.owner === me.value?.username));

/**
 * One ticket per page load: the page asks once, while the session still
 * waits for its connection. The ticket replaces the one in the chat's
 * result, and the session accepts exactly one connection — so this page and
 * the claude.ai app can never both be connected.
 */
const connect = shallowRef<RemoteConnectResult | null>(null);
const ticketProblem = ref<string | null>(null);
const asking = ref(false);
let asked = false;

async function askTicket() {
  if (asked) return;
  asked = true;
  asking.value = true;
  try {
    const res = await remoteTicket(id.value);
    connect.value = res.result;
    queryClient.setQueryData(["remote", "session", id.value], res.session);
  } catch (err) {
    ticketProblem.value = isProblem(err) && err.status === 409 ? (err.detail?.includes("already connected") ? t.alreadyActive : t.cannotConnect) : errorMessage(err);
  } finally {
    asking.value = false;
  }
}

onMounted(async () => {
  const s = session.data.value ?? (await session.refetch()).data;
  if (s && s.status === "pending" && (!s.owner || s.owner === me.value?.username)) await askTicket();
});

const appName = computed(() => (data.value ? remoteApp(data.value.kind) : null));
const app = useMcpApp(() => (connect.value ? appName.value : null));
const toolResult = computed(() => (connect.value ? { structuredContent: connect.value as unknown as Record<string, unknown> } : null));

const facts = computed<DescriptionItem[]>(() => {
  const d = data.value;
  if (!d) return [];
  return [
    { label: "Account", value: d.display, mono: true },
    ...(d.owner && !mine.value ? [{ label: t.owner, value: d.owner }] : []),
    { label: d.startedAt ? t.started : t.created, key: "started" },
    ...(d.durationSeconds !== null ? [{ label: t.duration, value: formatDuration(d.durationSeconds * 1000) }] : []),
    ...(d.bytesIn + d.bytesOut > 0 ? [{ label: t.traffic, value: `↑ ${formatBytes(d.bytesIn)} · ↓ ${formatBytes(d.bytesOut)}` }] : []),
  ];
});
const ended = computed(() => Boolean(data.value) && !isOpen(data.value!));
const transcript = useRemoteTranscript(() => (data.value?.kind === "ssh" && data.value.recorded && ended.value ? data.value.id : null));

const closing = ref(false);
async function close() {
  if (!data.value) return;
  const answer = await confirm({ title: `${t.close}: ${data.value.host}?`, description: t.closeConfirm, confirmLabel: t.close, destructive: true });
  if (!answer) return;
  closing.value = true;
  try {
    const updated = await closeRemoteSession(data.value.id);
    queryClient.setQueryData(["remote", "session", id.value], updated);
    await queryClient.invalidateQueries({ queryKey: ["remote", "sessions"] });
    toast.success(t.closed);
  } catch (err) {
    toast.error(errorMessage(err));
  } finally {
    closing.value = false;
  }
}
</script>

<template>
  <div>
    <ListSkeleton v-if="session.isPending.value" :rows="3" />
    <ErrorState v-else-if="session.error.value" :error="session.error.value" what="Remote session" @retry="session.refetch()" />
    <template v-else-if="data">
      <PageHeader :title="`${t.kinds[data.kind]} · ${data.host}`" :crumbs="[{ label: t.title, to: '/remote' }, { label: data.host }]">
        <template #badges>
          <Badge v-if="data.target" variant="outline" class="font-mono">{{ data.target }}</Badge>
          <StatusBadge :status="statusTone(data.status)" :label="t.status[data.status]" />
        </template>
        <template #meta>
          <DescriptionList :items="facts" layout="inline" dense class="mt-2">
            <template #started><span :title="formatDateTime(data.startedAt ?? data.createdAt)"><RelativeTime :value="data.startedAt ?? data.createdAt" /></span></template>
          </DescriptionList>
          <p v-if="data.reason" class="mt-1 text-sm [overflow-wrap:anywhere]"><span class="text-muted-foreground">{{ t.reason }}:</span> {{ data.reason }}</p>
        </template>
        <template v-if="isOpen(data)" #actions>
          <Button variant="outline" :disabled="closing" @click="close"><X /> {{ t.close }}</Button>
        </template>
      </PageHeader>

      <div class="flex flex-col gap-4">
        <p class="text-sm text-muted-foreground">{{ t.limits(data.maxMinutes, data.idleMinutes) }} {{ data.kind === "rdp" ? t.notRecorded : "" }}</p>

        <!-- Connect: this page's own ticket, the same app page claude.ai shows. -->
        <template v-if="connect">
          <Alert><AlertDescription>{{ t.singleUse }}</AlertDescription></Alert>
          <ListSkeleton v-if="app.isPending.value" :rows="4" />
          <ErrorState v-else-if="app.error.value" :error="app.error.value" what="App" @retry="app.refetch()" />
          <McpAppFrame
            v-else-if="app.data.value"
            :html="app.data.value.html"
            :meta="app.data.value._meta.ui"
            :title="`${t.kinds[data.kind]} ${data.host}`"
            :tool-input="{ kind: data.kind, host: data.host }"
            :tool-result="toolResult"
            :min-height="data.kind === 'rdp' ? 560 : 520"
          />
        </template>
        <p v-else-if="asking" class="text-sm text-muted-foreground" role="status">{{ t.connecting }}</p>
        <Alert v-else-if="ticketProblem" variant="destructive"><AlertDescription>{{ ticketProblem }} {{ t.newSession }}</AlertDescription></Alert>
        <Alert v-else-if="data.status === 'pending' && !mine"><AlertDescription>{{ t.notOwner }}</AlertDescription></Alert>
        <Alert v-else-if="data.status === 'active'"><AlertDescription>{{ t.alreadyActive }}</AlertDescription></Alert>

        <section v-if="ended" class="flex flex-col gap-3">
          <p v-if="data.closeReason" class="text-sm [overflow-wrap:anywhere]"><span class="text-muted-foreground">{{ t.closeReason }}:</span> {{ data.closeReason }}</p>
          <template v-if="data.kind === 'ssh' && data.recorded">
            <div class="flex flex-wrap items-center gap-3">
              <h2 class="section-title">{{ t.transcript }}</h2>
              <a :href="recordingUrl(data.id)" class="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" download><Download class="size-4" /> {{ t.downloadRecording }}</a>
            </div>
            <ListSkeleton v-if="transcript.isPending.value" :rows="3" />
            <ErrorState v-else-if="transcript.error.value" :error="transcript.error.value" :what="t.transcript" @retry="transcript.refetch()" />
            <template v-else-if="transcript.data.value">
              <p v-if="transcript.data.value.truncated || transcript.data.value.redactions" class="text-xs text-muted-foreground">
                <template v-if="transcript.data.value.truncated">{{ t.transcriptTruncated }} </template>
                <template v-if="transcript.data.value.redactions">{{ t.redactions(transcript.data.value.redactions) }}</template>
              </p>
              <CodeBlock :code="transcript.data.value.text" :title="`${data.display} · ${data.host}`" wrap max-height="36rem" />
            </template>
          </template>
          <p v-else-if="data.kind === 'ssh' && data.status !== 'expired'" class="text-sm text-muted-foreground">{{ t.recordingLater }}</p>
        </section>
      </div>
    </template>
  </div>
</template>
